import React, { useMemo, useCallback } from 'react';
import classNames from 'classnames';
import classes from './styles.module.scss';

/**
 * SegmentedControl
 * - 100% width of its container
 * - Items are equally distributed
 * - Gradient selected background slides underneath items
 *
 * Props:
 * - items: Array<{ value: string; label: React.ReactNode; disabled?: boolean }>
 * - value: string | undefined
 * - onChange: (value: string) => void
 * - className?: string
 * - ariaLabel?: string
 */
export default function SegmentedControl({
  items,
  selectedItem,
  onChange,
  className,
  ariaLabel,
  readOnly,
}) {
  const selectedIndex = useMemo(
    () =>
      Math.max(
        0,
        items.findIndex((i) => i.value === selectedItem?.value)
      ),
    [items, selectedItem]
  );

  const onItemClick = useCallback(
    (item) => () => {
      if (item.disabled || readOnly) return;
      if (item.value !== selectedItem?.value) {
        onChange?.(item);
      }
    },
    [onChange, selectedItem, readOnly]
  );

  const onKeyDown = useCallback(
    (e) => {
      if (!items?.length) return;
      if (readOnly) return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        const delta = e.key === 'ArrowRight' ? 1 : -1;
        // Find next enabled index
        let next = selectedIndex;
        for (let step = 0; step < items.length; step += 1) {
          next = (next + delta + items.length) % items.length;
          if (!items[next].disabled) break;
        }
        onChange?.(items[next]);
      }
    },
    [items, onChange, selectedIndex, readOnly]
  );

  return (
    <div
      className={classNames(classes.SegmentedControl, className)}
      style={{ '--count': items.length, '--index': selectedIndex }}
      role="tablist"
      tabIndex={0}
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
    >
      <div className={classes.slider} aria-hidden />
      {items.map((item, index) => {
        const isSelected = index === selectedIndex;
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={isSelected}
            tabIndex={isSelected ? 0 : -1}
            className={classNames(classes.item, {
              [classes.selected]: isSelected,
              [classes.disabled]: item.disabled,
            })}
            onClick={onItemClick(item, index)}
            disabled={item.disabled}
          >
            <div className={classes.labelContainer}>
              <div className={classes.label}>{item.label}</div>
              {item.count !== null && item.count !== undefined && (
                <div
                  className={classNames(classes.count, {
                    [classes.zero]: item.count === 0,
                    [classes.nonZero]: item.count > 0,
                    [classes.selected]: item.isSelected,
                  })}
                >
                  {item.count}
                </div>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}
